import type { MutationFunctionOptions } from '@apollo/client';

import type { SetAssetPayload } from '@graphql/generated-base';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useUploadPhotoFromUrlMutation } from './useContainerPhotoUpload.graphql.generated';
import { useSetEventPageAssetMutation } from '@graphql/mutations/registry.graphql.generated';
import type { SetEventPageAssetMutation, SetEventPageAssetMutationVariables } from '@graphql/mutations/registry.graphql.generated';

type SaveNativeEventPagePhotoArgs = Readonly<{
  previewUrl: string;
  containerId: string;
  eventPageId: string;
  assetPayload: SetAssetPayload;
  skipBlobRegistration?: boolean;
  setAssetOptions?: Omit<MutationFunctionOptions<SetEventPageAssetMutation, SetEventPageAssetMutationVariables>, 'variables'>;
}>;

// setEventPageAsset expects the blob in canonical media storage, so direct Filestack uploads are registered from previewUrl first.
export const useSaveNativeEventPagePhoto = () => {
  const [uploadPhotoFromUrl, { loading: isRegisteringBlob }] = useUploadPhotoFromUrlMutation();
  const [setEventPageAsset, { loading: isSettingPageAsset }] = useSetEventPageAssetMutation();

  const saveNativeEventPagePhoto = useEventCallback(
    async ({ containerId, eventPageId, previewUrl, assetPayload, skipBlobRegistration, setAssetOptions }: SaveNativeEventPagePhotoArgs) => {
      if (!skipBlobRegistration) {
        const { data } = await uploadPhotoFromUrl({
          variables: { payload: { assetId: assetPayload.assetId, containerId, url: previewUrl } },
        });
        if (!data?.uploadPhotoFromUrl?.id) {
          throw new Error('uploadPhotoFromUrl returned no photo');
        }
      }
      return setEventPageAsset({
        variables: { id: eventPageId, payload: assetPayload },
        ...setAssetOptions,
      });
    },
  );

  return { saveNativeEventPagePhoto, isSavingNativePagePhoto: isRegisteringBlob || isSettingPageAsset };
};
